import React, { useState, useEffect } from 'react';
import { Button, Form, Table, Badge, Alert, Modal } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';
import { isStretch, hasReps, hasDuration } from '../utils/exerciseTypes';

function SetLogger({ exercise, onUpdate, mode = 'logging' }) {
  const [sets, setSets] = useState(exercise.sets || []);
  const [exerciseHistory, setExerciseHistory] = useState([]); // Changed to array
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [resistanceOptions, setResistanceOptions] = useState([]);

  const isWeightReps = exercise.exercise_type === 'weight_reps';
  const isWeightTime = exercise.exercise_type === 'weight_time';
  // A stretch is set-shaped like the two above, but nothing is required: it may
  // be held for time, done for reps, loaded with weight, done against a band,
  // or any combination. So it opts into every column rather than one.
  const stretch = isStretch(exercise.exercise_type);
  const showReps = hasReps(exercise.exercise_type);
  const showDuration = hasDuration(exercise.exercise_type);
  const isTemplateMode = mode === 'template';

  useEffect(() => {
    // Only fetch history in logging mode
    if (!isTemplateMode) {
      fetchExerciseHistory();
    }
  }, [exercise.exercise_id, isTemplateMode]);

  useEffect(() => {
    // Only stretches show a resistance field, so only they need the list.
    if (!stretch) return;
    workoutAPI
      .fetchResistances()
      .then(setResistanceOptions)
      .catch((err) => {
        // Non-fatal: the field is free text, autocomplete is a convenience.
        console.error('Error fetching resistance options:', err);
      });
  }, [stretch]);

  useEffect(() => {
    // Update parent component when sets change
    onUpdate({ sets });
  }, [sets]);

  const fetchExerciseHistory = async () => {
    setLoadingHistory(true);
    try {
      const history = await workoutAPI.fetchExerciseHistory(exercise.exercise_id);
      if (history && history.length > 0) {
        setExerciseHistory(history.slice(0, 3)); // Last 3 sessions
      }
    } catch (err) {
      console.error('Error fetching exercise history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleAddSet = () => {
    const newSet = {
      set_number: sets.length + 1,
      weight: '',
      weight_unit: 'lbs',
      resistance: stretch ? '' : undefined,
      reps: showReps ? '' : undefined,
      reps_prescription: isWeightReps && isTemplateMode ? '' : undefined,
      duration_seconds: showDuration ? '' : undefined,
      notes: ''
    };
    setSets([...sets, newSet]);
  };

  const handleRemoveSet = (index) => {
    const confirmed = window.confirm('Delete this set?');
    if (!confirmed) return;

    const newSets = sets.filter((_, i) => i !== index);
    // Renumber sets
    const renumberedSets = newSets.map((set, i) => ({
      ...set,
      set_number: i + 1
    }));
    setSets(renumberedSets);
  };

  const handleSetChange = (index, field, value) => {
    const newSets = sets.map((set, i) => {
      if (i === index) {
        return { ...set, [field]: value };
      }
      return set;
    });
    setSets(newSets);
  };

  const handleCopyFromLast = () => {
    if (!exerciseHistory || exerciseHistory.length === 0 || !exerciseHistory[0].sets) return;

    const mostRecent = exerciseHistory[0];
    const copiedSets = mostRecent.sets.map((set, index) => ({
      set_number: index + 1,
      weight: set.weight || '',
      weight_unit: set.weight_unit || 'lbs',
      resistance: stretch ? (set.resistance || '') : undefined,
      reps: showReps ? (set.reps || '') : undefined,
      duration_seconds: showDuration ? (set.duration_seconds || '') : undefined,
      notes: ''
    }));

    setSets(copiedSets);
  };

  const formatSessionPerformance = (session) => {
    if (!session || !session.sets || session.sets.length === 0) {
      return 'No previous data';
    }

    const setCount = session.sets.length;
    const firstSet = session.sets[0];

    // Extract weights and check if they're all the same (handle null/undefined/empty as 0)
    const weights = session.sets.map(s => {
      const weight = parseFloat(s.weight);
      return isNaN(weight) ? 0 : weight;
    });
    const allSameWeight = weights.every(w => w === weights[0]);

    // Format weight display - show single weight if all same, otherwise show all
    const weightDisplay = allSameWeight
      ? `${weights[0]}`
      : weights.join('/');

    if (isWeightReps) {
      const reps = session.sets.map(s => s.reps).join('/');
      return `${setCount} set${setCount === 1 ? '' : 's'}: ${reps} reps @ ${weightDisplay} ${firstSet.weight_unit}`;
    } else if (isWeightTime) {
      const durations = session.sets.map(s => `${s.duration_seconds}s`).join('/');
      return `${setCount} set${setCount === 1 ? '' : 's'}: ${durations} @ ${weightDisplay} ${firstSet.weight_unit}`;
    } else if (stretch) {
      // Any field may be blank, so build the summary from whatever was filled
      // in rather than assuming a fixed shape.
      const parts = [];
      if (session.sets.some(s => s.duration_seconds)) {
        parts.push(session.sets.map(s => (s.duration_seconds ? `${s.duration_seconds}s` : '-')).join('/'));
      }
      if (session.sets.some(s => s.reps)) {
        parts.push(`${session.sets.map(s => s.reps || '-').join('/')} reps`);
      }
      if (weights.some(w => w > 0)) {
        parts.push(`@ ${weightDisplay} ${firstSet.weight_unit}`);
      }
      const bands = [...new Set(session.sets.map(s => s.resistance).filter(Boolean))];
      if (bands.length > 0) {
        parts.push(`(${bands.join(', ')})`);
      }
      const detail = parts.length > 0 ? parts.join(' ') : 'no detail recorded';
      return `${setCount} set${setCount === 1 ? '' : 's'}: ${detail}`;
    }

    return 'Unknown format';
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';

    const now = new Date();
    const past = new Date(date);
    const diffDays = Math.floor((now - past) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Get prescription display for a set (for logging mode)
  const getPrescriptionDisplay = (set) => {
    // First check reps_prescription, then fall back to reps for backward compatibility
    if (set.reps_prescription) {
      return set.reps_prescription;
    }
    // For old templates that only have numeric reps, show that
    if (set.reps !== undefined && set.reps !== '' && set.reps !== null) {
      return String(set.reps);
    }
    return null;
  };

  return (
    <div className="set-logger">
      {/* Last Performance Icon Button - only show in logging mode */}
      {!isTemplateMode && !loadingHistory && exerciseHistory && exerciseHistory.length > 0 && (
        <div className="mb-2">
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowPerformanceModal(true)}
            className="p-0 text-info"
            title="View last performance"
          >
            <i className="fas fa-info-circle me-1"></i>
            Last performance
          </Button>
        </div>
      )}

      {/* Sets Table */}
      {sets.length > 0 ? (
        <Table striped bordered size="sm" className="mb-3">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>Set</th>
              <th>{stretch ? 'Added weight' : 'Weight'}</th>
              <th style={{ width: '60px' }}>Unit</th>
              {stretch && <th>Resistance</th>}
              {showReps && <th>{isWeightReps && isTemplateMode ? 'Reps (Prescription)' : 'Reps'}</th>}
              {showDuration && <th>Duration (sec)</th>}
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sets.map((set, index) => (
              <tr key={index}>
                <td className="text-center">
                  <Badge bg="primary">{set.set_number}</Badge>
                </td>
                <td>
                  <Form.Control
                    type="number"
                    size="sm"
                    step="0.5"
                    placeholder="0"
                    value={set.weight}
                    onChange={(e) => handleSetChange(index, 'weight', e.target.value)}
                  />
                </td>
                <td>
                  <Form.Select
                    size="sm"
                    value={set.weight_unit}
                    onChange={(e) => handleSetChange(index, 'weight_unit', e.target.value)}
                  >
                    <option value="lbs">lbs</option>
                    <option value="kg">kg</option>
                  </Form.Select>
                </td>
                {stretch && (
                  <td>
                    {/* Free text, not a dropdown: band naming is personal and
                        unordered, so the datalist suggests without constraining.
                        Complements the numeric weight beside it - a weighted
                        dead hang can carry both. */}
                    <Form.Control
                      type="text"
                      size="sm"
                      list="resistance-options"
                      placeholder="e.g. red band"
                      value={set.resistance || ''}
                      onChange={(e) => handleSetChange(index, 'resistance', e.target.value)}
                    />
                  </td>
                )}
                {showReps && (
                  <td>
                    {isWeightReps && isTemplateMode ? (
                      /* Template mode: Text input for prescription (e.g., "8-10", "AMRAP") */
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="e.g., 8-10"
                        value={set.reps_prescription || ''}
                        onChange={(e) => handleSetChange(index, 'reps_prescription', e.target.value)}
                      />
                    ) : (
                      /* Logging mode: Show prescription label above numeric input */
                      <div className="reps-input-container">
                        {getPrescriptionDisplay(set) && (
                          <div className="reps-prescription">
                            {getPrescriptionDisplay(set)}
                          </div>
                        )}
                        <Form.Control
                          type="number"
                          size="sm"
                          placeholder="0"
                          value={set.reps || ''}
                          onChange={(e) => handleSetChange(index, 'reps', e.target.value)}
                        />
                      </div>
                    )}
                  </td>
                )}
                {showDuration && (
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      placeholder="0"
                      value={set.duration_seconds || ''}
                      onChange={(e) => handleSetChange(index, 'duration_seconds', e.target.value)}
                    />
                  </td>
                )}
                <td className="text-center">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveSet(index)}
                  >
                    <i className="fas fa-trash"></i>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div className="text-center py-3 mb-3 border rounded bg-light">
          <p className="text-muted mb-0">No sets added yet</p>
        </div>
      )}

      {/* One datalist for the whole table - every resistance input references
          it by id, so the browser dedupes the suggestion popup for us. */}
      {stretch && (
        <datalist id="resistance-options">
          {resistanceOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      )}

      {/* Add Set Button */}
      <Button
        variant="outline-primary"
        size="sm"
        onClick={handleAddSet}
        className="w-100"
      >
        <i className="fas fa-plus me-1"></i> Add Set
      </Button>

      {/* Performance Modal - only in logging mode */}
      {!isTemplateMode && (
        <Modal show={showPerformanceModal} onHide={() => setShowPerformanceModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Last {exerciseHistory.length} Performance{exerciseHistory.length > 1 ? 's' : ''}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {exerciseHistory && exerciseHistory.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {exerciseHistory.map((session, index) => (
                  <div
                    key={session.id || index}
                    className="mb-3 p-3 border rounded"
                    style={{ backgroundColor: index === 0 ? '#f8f9fa' : '#ffffff' }}
                  >
                    {/* Session Badge and Date */}
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Badge bg={index === 0 ? 'primary' : 'secondary'}>
                        {index === 0 ? 'Most Recent' : `${index + 1} session${index === 1 ? '' : 's'} ago`}
                      </Badge>
                      <small className="text-muted">
                        {new Date(session.workout_date).toLocaleDateString()} ({formatTimeAgo(session.workout_date)})
                      </small>
                    </div>

                    {/* Performance Data */}
                    <div>
                      <strong>{formatSessionPerformance(session)}</strong>
                    </div>

                    {/* Session Notes */}
                    {session.notes && (
                      <div className="mt-2 pt-2 border-top">
                        <small className="text-muted d-block">Notes:</small>
                        <div className="text-muted" style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                          {session.notes}
                        </div>
                      </div>
                    )}

                    {/* Workout Name */}
                    {session.workout_name && (
                      <div className="mt-1">
                        <small className="text-muted">
                          <i className="fas fa-dumbbell me-1"></i>
                          {session.workout_name}
                        </small>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted mb-0">No previous performance data</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowPerformanceModal(false)}>
              Close
            </Button>
            {exerciseHistory && exerciseHistory.length > 0 && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  handleCopyFromLast();
                  setShowPerformanceModal(false);
                }}
              >
                <i className="fas fa-copy me-1"></i> Copy Most Recent
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default SetLogger;
